import { formatValue } from "../utils/helpers";
import metricMetadataIcon from '../assets/metric-metadata-icon.svg';
import { useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, GeoDetails, GeoTag, MetricMetadata } from "../services/types";
import { calculateGeoPrefix, fetchMetricMetadata, fetchNationalData, fetchStateData, getGeoPrefix } from "../utils/dashboardUtils";
import { useDashboardColor } from "../context/DashboardColorContext";
import DefaultIcon from '../assets/svgr/default-metric.svg?react';
import GradientBar from "./GradientBar";

interface MetricCardProps {
    metric: DisaggregatedMetric;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    svg?: React.FC<React.SVGProps<SVGSVGElement>>;
    showMetadataIcon?: boolean;
};

const MetricCard = ({ metric, disaggData, geoDetails, geoTag, svg: SvgIcon, showMetadataIcon = true }: MetricCardProps) => {
    const color = useDashboardColor();
    // @ts-ignore
    const IconComponent = SvgIcon || DefaultIcon;

    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [stateData, setStateData] = useState<DisaggregatedMetric[] | null>(null);
    const [nationalData, setNationalData] = useState<DisaggregatedMetric[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Destructure metric properties
    const { id, name, versions } = metric;
    const { value, unit, unit_descriptor } = versions[0];

    // Refs for metadata modal
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${id ?? 'main'}`;

    const fetchData = async (fetchFunction: Function, setState: React.Dispatch<React.SetStateAction<any>>, dataState: any, id: number, disaggregated?: boolean, geoPrefix?: string) => {
        if (dataState) return;  // If data is already fetched, return early

        setLoading(true);
        try {
            const result = await fetchFunction(id, disaggregated, geoPrefix);
            setState(result ? result.data : null);
        } catch (error) {
            setError(error as Error);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);
    
        const fetchPromises: Promise<void>[] = [];
    
        // Fetch metadata if not already fetched
        if (!metadata) {
            fetchPromises.push(fetchData(fetchMetricMetadata, setMetadata, metadata, id));
        }
    
        // Only proceed with state and national data fetch if the unit is 'percentage' or 'ratio'
        if (unit === 'percentage' || unit === 'ratio') {
            // Determine geoPrefix
            const geoPrefix = geoTag?.value 
                ? getGeoPrefix(geoTag.value) 
                : (stateData ? null : calculateGeoPrefix(geoDetails));
    
            // Fetch state data if not already fetched
            if (!stateData && geoPrefix) {
                fetchPromises.push(fetchData(fetchStateData, setStateData, stateData, id, false, geoPrefix));
            }
    
            // Fetch national data if not already fetched
            if (!nationalData) {
                fetchPromises.push(fetchData(fetchNationalData, setNationalData, nationalData, id, false));
            }
        }
    
        // Execute all pending fetch operations in parallel
        if (fetchPromises.length > 0) {
            await Promise.all(fetchPromises);
        }
    };

    return (
        <div 
            className="relative overflow-hidden group hover:shadow-lg transition-all border rounded-xl bg-white" 
        >
            <div className="p-3">
                <div className="flex items-start justify-between">
                    <h3 className="text-xs font-semibold text-color-cai-brown text-left break-words">
                        {name}
                    </h3>
                    {/* Metadata Icon */}
                    {showMetadataIcon && 
                        <button
                            type="button"
                            onClick={handleClick}
                            aria-haspopup="dialog"
                            aria-expanded={showMetadata ? 'true' : 'false'}
                            aria-controls={modalId}
                            aria-label={`Additional information about ${name}`}
                            className="
                            flex items-start justify-center h-6 w-6 rounded
                            transition-transform hover:scale-105 focus:scale-105
                            focus-visible:outline-none
                            focus-visible:ring-2 focus-visible:ring-offset-2
                            focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                            dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                            "
                        >
                            <img src={metricMetadataIcon} alt="" aria-hidden="true" className="h-3 w-3" draggable="false" />
                        </button>
                    }
                </div>

                <div className="space-y-0.5 flex flex-col items-start mt-0.5">
                    <span className="text-3xl font-semibold leading-none" style={{ color }}>
                        {formatValue(value, unit, id)}
                    </span>
                    
                    {unit_descriptor && 
                        <p className="text-xs text-muted-foreground leading-tight">
                            {unit_descriptor}
                        </p>
                    }
                </div>
            </div>
            <GradientBar color={color} />

            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                metadata={metadata}
                stateData={stateData}
                nationalData={nationalData}
                disaggData={disaggData}
                // svg={svg}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </div>
    );
};


export default MetricCard;
